import { Form } from "antd";
import dayjs from "dayjs";

const SHORT_MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/**
 * Generates all YYYY-MM-DD date strings between startDate and endDate inclusive.
 */
function getDatesInRange(startDate, endDate) {
  if (!startDate || !endDate) return [];
  const dates = [];
  let current = dayjs(startDate);
  const end = dayjs(endDate);
  while (current.isBefore(end) || current.isSame(end, "day")) {
    dates.push(current.format("YYYY-MM-DD"));
    current = current.add(1, "day");
  }
  return dates;
}

function formatChipLabel(dateStr) {
  const d = dayjs(dateStr);
  return `${SHORT_MONTHS[d.month()]} ${d.date()}`;
}

/**
 * AbsenceDateSelectorField – renders chips for each date in the selected period.
 * Clicking a chip toggles it as an absent date.
 *
 * Uses Form.useWatch to react to start_date / end_date changes from the
 * period selector.
 *
 * Integrates with Ant Design Form.Item via value / onChange.
 * value = string[] of selected absent date strings ("YYYY-MM-DD")
 */
const AbsenceDateSelectorField = ({ value = [], onChange, form }) => {
  const startDate = Form.useWatch("start_date", form);
  const endDate = Form.useWatch("end_date", form);

  const dates = getDatesInRange(startDate, endDate);

  if (dates.length === 0) {
    return (
      <span style={{ color: "#aaa", fontSize: 13 }}>
        Select a period first to choose absence dates.
      </span>
    );
  }

  const selected = new Set(Array.isArray(value) ? value : []);

  const toggleDate = (dateStr) => {
    const next = new Set(selected);
    if (next.has(dateStr)) {
      next.delete(dateStr);
    } else {
      next.add(dateStr);
    }
    onChange?.(Array.from(next).sort());
  };

  const chipBase = {
    display: "inline-flex",
    alignItems: "center",
    padding: "3px 10px",
    margin: "3px",
    borderRadius: 16,
    fontSize: 13,
    cursor: "pointer",
    border: "1px solid #d9d9d9",
    userSelect: "none",
    transition: "background 0.15s, color 0.15s",
  };

  const selectedChip = {
    ...chipBase,
    backgroundColor: "#ff4d4f",
    color: "#fff",
    borderColor: "#ff4d4f",
  };

  const normalChip = {
    ...chipBase,
    backgroundColor: "#fff",
    color: "#000",
  };

  return (
    <div style={{ display: "flex", flexWrap: "wrap" }}>
      {dates.map((dateStr) => (
        <span
          key={dateStr}
          style={selected.has(dateStr) ? selectedChip : normalChip}
          onClick={() => toggleDate(dateStr)}
          title={selected.has(dateStr) ? "Click to unmark as absent" : "Click to mark as absent"}
        >
          {formatChipLabel(dateStr)}
        </span>
      ))}
    </div>
  );
};

export default AbsenceDateSelectorField;
